import FileSaver from "file-saver";
import { vec3 } from "gl-matrix";
import { Leva, button, buttonGroup, folder, useControls } from "leva";
import { ButtonGroupOpts, Schema } from "leva/dist/declarations/src/types";
import { memo, useEffect, useRef, useState } from "react";

import { DownloadProgress } from "../rs/cache/CacheFiles";
import { isTouchDevice } from "../util/DeviceUtil";
import { lerp, slerp } from "../util/MathUtil";
import { loadCacheFiles } from "./Caches";
import { CameraView, ProjectionType } from "./Camera";
import { ExportType, MapViewer } from "./MapViewer";
import { MapViewerRenderer } from "./MapViewerRenderer";
import {
    MapViewerRendererType,
    createRenderer,
    getAvailableRenderers,
    getRendererName,
} from "./MapViewerRenderers";
import { fetchNpcSpawns, getNpcSpawnsUrl } from "./data/npc/NpcSpawn";
import { WebGLMapViewerRenderer } from "./webgl/WebGLMapViewerRenderer";

interface MapViewerControlsProps {
    renderer: MapViewerRenderer;
    hideUi: boolean;
    setRenderer: (renderer: MapViewerRenderer) => void;
    setHideUi: (hideUi: boolean | ((hideUi: boolean) => boolean)) => void;
    setDownloadProgress: (progress: DownloadProgress | undefined) => void;
}

enum VarType {
    VARP = 0,
    VARBIT = 1,
}

export const MapViewerControls = ({
    renderer,
    hideUi: hidden,
    setRenderer,
    setHideUi,
    setDownloadProgress,
}: MapViewerControlsProps): JSX.Element => {
    const mapViewer = renderer.mapViewer;

    const [projectionType, setProjectionType] = useState<ProjectionType>(
        mapViewer.camera.projectionType,
    );

    const [minecraftPlayers, setMinecraftPlayers] = useState<
        Array<{ name: string; x: number; z: number }>
    >([]);
    const mcFollow = useRef<ReturnType<typeof setInterval> | null>();

    const [isExportingSprites, setExportingSprites] = useState(false);
    const [isExportingTextures, setExportingTextures] = useState(false);

    const positionControls = isTouchDevice
        ? "Left joystick, Drag up and down."
        : "WASD,\nR or E (up),\nF or C (down),\nUse SHIFT to go faster, or TAB to go slower.";
    const directionControls = isTouchDevice
        ? "Right joystick."
        : "Arrow Keys or Click and Drag. Double click for pointerlock.";

    const [varType, setVarType] = useState<VarType>(VarType.VARBIT);
    const [varId, setVarId] = useState(0);
    const [varValue, setVarValue] = useState(0);

    const controlsSchema: Schema = {
        Position: { value: positionControls, editable: false },
        Direction: { value: directionControls, editable: false },
    };

    const getMinecraftPlayers = async () => {
        try {
            const resp = await fetch(
                `https://map.07craft.com/up/world/474/${new Date().getTime()}`,
            );
            const players = [];
            if (resp.status === 200) {
                const data = await resp.json();
                const p = (data.players || []).filter(
                    (pd: { world: string; type: string }) =>
                        pd.world === "474" && pd.type === "player",
                );
                players.push(...p);
            }
            setMinecraftPlayers(players);
            return players;
        } catch (e) {
            console.error(e);
            setMinecraftPlayers([]);
            return [];
        }
    };

    useEffect(() => {
        getMinecraftPlayers();
    }, []);

    const [animationDuration, setAnimationDuration] = useState(10);
    const [cameraPoints, setCameraPoints] = useState<CameraView[]>(() => []);

    const addPoint = () => {
        setCameraPoints((pts) => [
            ...pts,
            {
                position: vec3.fromValues(
                    mapViewer.camera.pos[0],
                    mapViewer.camera.pos[1],
                    mapViewer.camera.pos[2],
                ),
                pitch: mapViewer.camera.pitch,
                yaw: mapViewer.camera.yaw,
                fov: mapViewer.camera.fov,
                orthoZoom: mapViewer.camera.orthoZoom,
            },
        ]);
    };

    const removeLastPoint = () => {
        setCameraPoints((pts) => pts.slice(0, pts.length - 1));
    };

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.repeat) {
                return;
            }

            switch (e.key) {
                case "F1":
                    setHideUi((v) => !v);
                    break;
                case "F2":
                    setCameraRunning((v) => !v);
                    break;
                case "F3":
                    addPoint();
                    break;
                case "F4":
                    removeLastPoint();
                    break;
            }
        }

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [mapViewer]);

    useEffect(() => {
        setPointControls(
            folder(
                cameraPoints.reduce((acc: Record<string, any>, v, i) => {
                    const point = v;
                    const buttons: ButtonGroupOpts = {
                        Teleport: () => mapViewer.setCamera(point),
                        Delete: () => setCameraPoints((pts) => pts.filter((_, j) => j !== i)),
                    };
                    acc["Point " + i] = buttonGroup(buttons);
                    return acc;
                }, {}),
            ),
        );
    }, [mapViewer, cameraPoints]);

    const [pointsControls, setPointControls] = useState(folder({}));
    const [isCameraRunning, setCameraRunning] = useState(false);

    useEffect(() => {
        if (!isCameraRunning) {
            return;
        }

        const segmentCount = cameraPoints.length - 1;
        // Need at least 2 points to start
        if (segmentCount <= 0) {
            setCameraRunning(false);
            return;
        }

        let animationId = -1;

        let start: number;
        const animate = (time: DOMHighResTimeStamp) => {
            if (!start) {
                start = time;
            }

            const elapsed = time - start;
            const overallProgress = elapsed / (animationDuration * 1000);

            const startIndex = Math.floor(overallProgress * segmentCount);
            const endIndex = startIndex + 1;
            const from = cameraPoints[startIndex];
            const to = cameraPoints[endIndex];
            const localProgress = (overallProgress * segmentCount) % 1;

            const isComplete = elapsed > animationDuration * 1000;
            if (isComplete) {
                setCameraRunning(false);
                mapViewer.setCamera(cameraPoints[cameraPoints.length - 1]);
                return;
            }
            const newView: CameraView = {
                position: vec3.fromValues(
                    lerp(from.position[0], to.position[0], localProgress),
                    lerp(from.position[1], to.position[1], localProgress),
                    lerp(from.position[2], to.position[2], localProgress),
                ),
                pitch: lerp(from.pitch, to.pitch, localProgress),
                yaw: slerp(from.yaw, to.yaw, localProgress, 2048),
                fov: lerp(from.fov, to.fov, localProgress),
                orthoZoom: lerp(from.orthoZoom, to.orthoZoom, localProgress),
            };
            mapViewer.setCamera(newView);

            animationId = requestAnimationFrame(animate);
        };

        // Start animating
        animationId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(animationId);
        };
    }, [mapViewer, cameraPoints, animationDuration, isCameraRunning]);

    const rendererOptions: Record<string, MapViewerRendererType> = {};
    for (let v of getAvailableRenderers()) {
        rendererOptions[getRendererName(v)] = v;
    }

    const recordSchema: Schema = {
        "Add point (F3)": button(() => addPoint()),
        "Delete last point (F4)": button(() => removeLastPoint()),
        Length: {
            value: animationDuration,
            onChange: (v: number) => {
                setAnimationDuration(v);
            },
        },
        Points: pointsControls,
    };

    if (isCameraRunning) {
        const buttonName = "Stop (F2)";
        recordSchema[buttonName] = button(() => setCameraRunning(false));
        recordSchema[buttonName].order = -1;
    } else {
        const buttonName = "Start (F2)";
        recordSchema[buttonName] = button(() => setCameraRunning(true));
        recordSchema[buttonName].order = -1;
    }

    const teleportToMcPlayer = async (player: string, incremental = false, fetchFirst = false) => {
        let players = minecraftPlayers;
        if (fetchFirst) players = await getMinecraftPlayers();
        if (player.length === 0) return;
        const newPos = players.find((p) => p.name === player);
        if (newPos === undefined) return;
        const mcX = newPos.x;
        const mcZ = newPos.z;
        const xOffset = 1994;
        const yOffset = 4004;
        const rsX = Math.floor((1 + Math.floor(Number(mcX))) / 2 + xOffset);
        const rsY = Math.floor(-((1 + Math.floor(Number(mcZ))) / 2) + yOffset);
        mapViewer.camera.reposition(rsX, rsY);
        // if (incremental) {
        //     const curr = mapViewer.camera.pos;
        //     mapViewer.camera.move(rsX, 0, rsY);
        // } else {
        // }
    };

    const [, set07craft, get07craft] = useControls(
        "07craft",
        () => ({
            enableGrid: {
                label: "Grid",
                value: false,
                transient: false,
                onChange: (v: boolean) => {
                    renderer.setTileGrid(v);
                },
            },
            teleport: {
                label: "Teleport",
                value: {
                    x: Math.floor(mapViewer.camera.pos[0]),
                    y: Math.floor(mapViewer.camera.pos[2]),
                },
                step: 1,
                joystick: false,
            },
            resetPos: buttonGroup({
                label: "",
                opts: {
                    Go: () => {
                        const p = get07craft("teleport");
                        if (
                            p.x !== mapViewer.camera.getPosX() &&
                            p.y !== mapViewer.camera.getPosZ()
                        ) {
                            mapViewer.camera.reposition(p.x, p.y);
                        }
                    },
                    Refresh: () => {
                        set07craft({
                            teleport: {
                                x: mapViewer.camera.getPosX(),
                                y: mapViewer.camera.getPosZ(),
                            },
                        });
                    },
                    Reset: () => {
                        mapViewer.camera.reposition(3242, 3202);
                        set07craft({ teleport: { x: 3242, y: 3202 } });
                    },
                },
            }),
            Players: folder({
                mcPlayer: {
                    value: "",
                    label: "Player",
                    options: {
                        [minecraftPlayers.length > 0 ? "<none>" : "No players online"]: "",
                        ...Object.fromEntries(minecraftPlayers.map((p) => [p.name, p.name])),
                    },
                    disabled: minecraftPlayers.length === 0,
                },
                mcTeleport: buttonGroup({
                    label: "",
                    opts: {
                        Refresh: () => getMinecraftPlayers(),
                        "Teleport To": () => {
                            teleportToMcPlayer(get07craft("mcPlayer"), false, true);
                        },
                    },
                }),
                mcFollow: {
                    label: "Follow",
                    value: false,
                    disabled: minecraftPlayers.length === 0,
                    transient: false,
                    onChange: (v: boolean) => {
                        if (v && get07craft("mcPlayer").length) {
                            if (mcFollow.current) clearInterval(mcFollow.current);
                            mcFollow.current = setInterval(() => {
                                teleportToMcPlayer(get07craft("mcPlayer"), true, true);
                            }, 1000);
                        } else {
                            if (mcFollow.current) clearInterval(mcFollow.current);
                            mcFollow.current = undefined;
                        }
                    },
                },
            }),
            "Model Export": folder(
                {
                    bottomLeftBounds: {
                        label: "South West",
                        value: {
                            x: Math.floor(mapViewer.camera.pos[0]),
                            y: Math.floor(mapViewer.camera.pos[2]),
                        },
                        step: 1,
                        joystick: false,
                    },
                    topRightBounds: {
                        label: "North East",
                        value: {
                            x: Math.floor(mapViewer.camera.pos[0]),
                            y: Math.floor(mapViewer.camera.pos[2]),
                        },
                        step: 1,
                        joystick: false,
                    },
                    exportPos: buttonGroup({
                        label: "Refresh",
                        opts: {
                            "South West": () => {
                                set07craft({
                                    bottomLeftBounds: {
                                        x: mapViewer.camera.getPosX(),
                                        y: mapViewer.camera.getPosZ(),
                                    },
                                });
                            },
                            "North East": () => {
                                set07craft({
                                    topRightBounds: {
                                        x: mapViewer.camera.getPosX(),
                                        y: mapViewer.camera.getPosZ(),
                                    },
                                });
                            },
                        },
                    }),
                    exportMode: {
                        value: "unique_ids",
                        label: "Mode",
                        options: {
                            "Unique Model IDs": "unique_ids",
                            "MID grouped by Position": "positions",
                            "Pos grouped by MID": "positions_grouped",
                            "All": "all"
                        },
                    },
                    exportNames: {
                        label: "Names",
                        value: true,
                    },
                    "Export": button(() => {
                        const bl = get07craft("bottomLeftBounds");
                        const tr = get07craft("topRightBounds");
                        const mode = get07craft("exportMode");
                        const inclNames = get07craft("exportNames");
                        const locs = mapViewer.getDecodedLocs(bl, tr);
                        const names: { [id: string]: string } = {};
                        const types = new Set();
                        let obj: any = {
                            selection: {
                                southwest: {
                                    x: Math.floor(bl.x),
                                    y: Math.floor(bl.y),
                                },
                                northeast: {
                                    x: Math.floor(tr.x),
                                    y: Math.floor(tr.y),
                                },
                            }
                        };

                        if (mode === 'unique_ids') {
                            const modelIds = new Set(
                                locs.flatMap((loc) => {
                                    types.add(loc.type);
                                    if (loc.name && loc.name !== "null") names[loc.models] = loc.name;
                                    return loc.models.split(",").map(Number);
                                }),
                            );
                            obj.selection['types'] = Array.from(types);
                            obj['unique_models'] = Array.from(modelIds)
                        }

                        if (mode === 'positions') {
                            const byCoord: { [id: string]: string } = {};
                            for (const loc of locs) {
                                const key = `${loc.x},${loc.y},${loc.level}`;
                                types.add(loc.type);
                                if (byCoord[key]) {
                                    byCoord[key] = byCoord[key] + "," + loc.models;
                                } else {
                                    byCoord[key] = loc.models;
                                }
                                if (loc.name && loc.name !== "null") names[loc.models] = loc.name;
                            }

                            obj.selection['types'] = Array.from(types);
                            obj['models'] = byCoord;
                        }

                        if (mode === 'positions_grouped') {
                            const byCoord: { [id: string]: string[] } = {};
                            for (const loc of locs) {
                                const key = loc.models;
                                const pos = `${loc.x},${loc.y},${loc.level}`;
                                types.add(loc.type);
                                if (byCoord[key]) {
                                    byCoord[key].push(pos);
                                } else {
                                    byCoord[key] = [pos];
                                }
                                if (loc.name && loc.name !== "null") names[loc.models] = loc.name;
                            }

                            obj.selection['types'] = Array.from(types);
                            obj['models'] = byCoord;
                        }

                        if (mode === 'all') {
                            obj['model_data'] = locs;
                        }

                        if (types.size) obj.selection['types'] = Array.from(types);
                        if (inclNames && Object.keys(names).length) obj.selection['names'] = names;

                        const url = URL.createObjectURL(
                            new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" }),
                        );
                        window.open(url, "_blank");
                        URL.revokeObjectURL(url);
                    })
                },
                {
                    collapsed: true,
                },
            ),
        }),
        [minecraftPlayers],
    );

    useEffect(() => {
        // Clear selected player if they have disconnected, disable follow
        const mcPlayer = get07craft("mcPlayer");
        const newPos = minecraftPlayers.find((p) => p.name === mcPlayer);
        if (mcPlayer.length && newPos === undefined) {
            set07craft({ mcPlayer: "", mcFollow: false });
            if (mcFollow.current) clearInterval(mcFollow.current);
        }
    }, [minecraftPlayers]);

    useControls(
        {
            /* Links: folder(
                    {
                        GitHub: button(() => {
                            window.open("https://github.com/dennisdev/rs-map-viewer", "_blank");
                        }),
                    },
                    { collapsed: true },
                ), */
            Camera: folder(
                {
                    Projection: {
                        value: projectionType,
                        options: {
                            Perspective: ProjectionType.PERSPECTIVE,
                            Ortho: ProjectionType.ORTHO,
                        },
                        onChange: (v: ProjectionType) => {
                            mapViewer.camera.setProjectionType(v);
                            setProjectionType(v);
                        },
                        order: 0,
                    },
                    ...createCameraControls(mapViewer),
                    Speed: {
                        value: mapViewer.cameraSpeed,
                        min: 0.1,
                        max: 5,
                        step: 0.1,
                        onChange: (v: number) => {
                            mapViewer.cameraSpeed = v;
                        },
                        order: 10,
                    },
                    Controls: folder(controlsSchema, { collapsed: true, order: 999 }),
                },
                { collapsed: true },
            ),
            Distance: folder(
                {
                    Render: {
                        value: mapViewer.renderDistance,
                        min: 16,
                        max: 2000,
                        step: 16,
                        onChange: (v: number) => {
                            mapViewer.renderDistance = v;
                        },
                    },
                    Unload: {
                        value: mapViewer.unloadDistance,
                        min: 1,
                        max: 30,
                        step: 1,
                        onChange: (v: number) => {
                            mapViewer.unloadDistance = v;
                        },
                    },
                    Lod: {
                        value: mapViewer.lodDistance,
                        min: 0,
                        max: 30,
                        step: 1,
                        onChange: (v: number) => {
                            mapViewer.lodDistance = v;
                        },
                    },
                },
                { collapsed: false },
            ),
            Cache: folder(
                {
                    Version: {
                        value: mapViewer.loadedCache.info.name,
                        options: Object.fromEntries(
                            mapViewer.cacheList.caches.map((cache) => [
                                cache.name
                                    .replace("osrs-", "OSRS ")
                                    .replace("rs2-", "RS2 ")
                                    .replace("_", " - "),
                                cache.name,
                            ]),
                        ),
                        onChange: async (v: string) => {
                            const cacheInfo = mapViewer.cacheList.caches.find(
                                (cache) => cache.name === v,
                            );
                            if (v !== mapViewer.loadedCache.info.name && cacheInfo) {
                                const [loadedCache, npcSpawns] = await Promise.all([
                                    loadCacheFiles(cacheInfo, undefined, setDownloadProgress),
                                    fetchNpcSpawns(getNpcSpawnsUrl(cacheInfo)),
                                ]);
                                mapViewer.npcSpawns = npcSpawns;
                                mapViewer.initCache(loadedCache);
                                setDownloadProgress(undefined);
                            }
                        },
                    },
                },
                { collapsed: false },
            ),
            Render: folder(
                {
                    Renderer: {
                        value: renderer.type,
                        options: rendererOptions,
                        onChange: (v: MapViewerRendererType) => {
                            if (renderer.type !== v) {
                                const renderer = createRenderer(v, mapViewer);
                                mapViewer.setRenderer(renderer);
                                setRenderer(renderer);
                            }
                        },
                    },
                    "Fps Limit": {
                        value: renderer.fpsLimit,
                        min: 1,
                        max: 999,
                        onChange: (v: number) => {
                            renderer.fpsLimit = v;
                        },
                    },
                    ...renderer.getControls(),
                },
                { collapsed: true },
            ),
            /* Vars: folder(
                    {
                        Type: {
                            value: varType,
                            options: {
                                Varplayer: VarType.VARP,
                                Varbit: VarType.VARBIT,
                            },
                            onChange: setVarType,
                        },
                        Id: {
                            value: varId,
                            step: 1,
                            onChange: setVarId,
                        },
                        Value: {
                            value: varValue,
                            step: 1,
                            onChange: setVarValue,
                        },
                        Set: button(() => {
                            const varManager = mapViewer.varManager;
                            let updated = false;
                            if (varType === VarType.VARP) {
                                updated = varManager.setVarp(varId, varValue);
                            } else {
                                updated = varManager.setVarbit(varId, varValue);
                            }
                            if (updated) {
                                mapViewer.updateVars();
                                mapViewer.renderer.mapManager.clearMaps();
                            }
                        }),
                        Clear: button(() => {
                            mapViewer.varManager.clear();
                            mapViewer.updateVars();
                            mapViewer.renderer.mapManager.clearMaps();
                        }),
                    },
                    { collapsed: true },
                ), */
            Menu: folder(
                {
                    Tooltips: {
                        value: mapViewer.tooltips,
                        onChange: (v: boolean) => {
                            mapViewer.tooltips = v;
                        },
                    },
                    "Debug Id": {
                        value: mapViewer.debugId,
                        onChange: (v: boolean) => {
                            mapViewer.debugId = v;
                        },
                    },
                },
                { collapsed: true },
            ),
            // Record: folder(recordSchema, { collapsed: true }),
            /* Export: folder(
                    {
                        "Export Sprites": button(
                            () => {
                                if (isExportingSprites) {
                                    return;
                                }
                                setExportingSprites(true);
                                mapViewer.workerPool
                                    .exportSprites()
                                    .then((zipBlob) => {
                                        FileSaver.saveAs(
                                            zipBlob,
                                            `sprites_${mapViewer.loadedCache.info.name}.zip`,
                                        );
                                    })
                                    .finally(() => {
                                        setExportingSprites(false);
                                    });
                            },
                            { disabled: isExportingSprites },
                        ),
                        "Export Textures": button(
                            () => {
                                if (isExportingTextures) {
                                    return;
                                }
                                setExportingTextures(true);
                                mapViewer.workerPool
                                    .exportTextures()
                                    .then((zipBlob) => {
                                        FileSaver.saveAs(
                                            zipBlob,
                                            `textures_${mapViewer.loadedCache.info.name}.zip`,
                                        );
                                    })
                                    .finally(() => {
                                        setExportingTextures(false);
                                    });
                            },
                            { disabled: isExportingTextures },
                        ),
                    },
                    { collapsed: true },
                ), */
        },
        [
            renderer,
            projectionType,
            varType,
            varId,
            varValue,
            pointsControls,
            isCameraRunning,
            isExportingSprites,
            isExportingTextures,
        ],
    );

    return (
        <Leva
            titleBar={{ filter: false }}
            collapsed={false}
            hideCopyButton={true}
            hidden={hidden}
        />
    );
};

function createCameraControls(mapViewer: MapViewer): Schema {
    if (mapViewer.camera.projectionType === ProjectionType.PERSPECTIVE) {
        return {
            FOV: {
                value: mapViewer.camera.fov,
                min: 30,
                max: 140,
                step: 1,
                onChange: (v: number) => {
                    mapViewer.camera.fov = v;
                },
            },
        };
    } else {
        return {
            "Ortho Zoom": {
                value: mapViewer.camera.orthoZoom,
                min: 1,
                max: 60,
                step: 1,
                onChange: (v: number) => {
                    mapViewer.camera.orthoZoom = v;
                },
            },
        };
    }
}
